import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../api";
import { toasts } from "../components/AppToast";
import Layout from "../components/Layout";
import { FiTrash2, FiEdit, FiPlus, FiSearch } from "react-icons/fi";

export default function ManageEngcapacity() {
  const navigate = useNavigate();

  // Table state
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Delete modal state
  const [delModal, setDelModal] = useState(null);
  const [delSaving, setDelSaving] = useState(false);

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login");
    }
  }, [navigate]);

  // Fetch engine capacity data
  const fetchData = useCallback(async (pg, q) => {
    setLoading(true);

    try {
      const res = await API.get("/engcapacity", {
        params: {
          page: pg,
          limit: 10,
          search: q,
        },
      });

      setData(res.data?.data || []);
      setTotalPages(Number(res.data?.totalPages || 1));
      setTotal(Number(res.data?.total || 0));
    } catch (error) {
      console.error("Fetch Engine Capacity error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to load Engine Capacity."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // Load table when page or search changes
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData(page, search);
    }, 350);

    return () => clearTimeout(timer);
  }, [page, search, fetchData]);

  // Add or update engine capacity
  const handleSubmit = async () => {
    // Validate input
    if (!name.trim()) {
      toasts.warning("Enter Engine Capacity");
      return;
    }

    setSaving(true);
    setLoading(true);

    try {
      // Update
      if (editId) {
        await API.put(`/engcapacity/${editId}`, {
          name: name.trim(),
        });

        toasts.success("Engine Capacity updated successfully");
      } else {
        // Add
        await API.post("/engcapacity", {
          name: name.trim(),
        });

        toasts.success("Engine Capacity added successfully");
      }

      // Reset form
      setName("");
      setEditId(null);

      // Reload table
      await fetchData(page, search);
    } catch (error) {
      console.error("Submit error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Operation failed"
      );
    } finally {
      setSaving(false);
      setLoading(false);
    }
  };

  // Edit engine capacity
  const handleEdit = (row) => {
    setName(row.mstmod_val);
    setEditId(row.mstmod_id);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // Cancel edit
  const handleCancel = () => {
    setName("");
    setEditId(null);
  };

  // Open delete modal
  const openDeleteModal = (row) => {
    setDelModal(row);
  };

  // Close delete modal
  const closeDeleteModal = () => {
    if (delSaving) {
      return;
    }

    setDelModal(null);
  };

  // Delete engine capacity
  const handleDelete = async () => {
    if (!delModal) {
      return;
    }

    setDelSaving(true);
    setLoading(true);

    try {
      const deletedId = delModal.mstmod_id;

      await API.delete(`/engcapacity/${deletedId}`);

      toasts.success("Engine Capacity deleted successfully");

      // Close modal
      setDelModal(null);

      // Calculate remaining records
      const remainingRecords = Math.max(0, total - 1);

      const newTotalPages = Math.max(
        1,
        Math.ceil(remainingRecords / 10)
      );

      // Move to previous page if current page becomes empty
      if (page > 1 && page > newTotalPages) {
        setPage((p) => Math.max(1, p - 1));
      } else {
        await fetchData(page, search);
      }

      // Clear edit form if deleted item was being edited
      if (editId === deletedId) {
        setName("");
        setEditId(null);
      }
    } catch (error) {
      console.error("Delete error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Delete failed"
      );
    } finally {
      setDelSaving(false);
      setLoading(false);
    }
  };

  return (
    <Layout activePage="Engcapacity Details">
      <main className="dashboard-main">
        {/* Main card */}

        <div className="season-card">
          {/* Top controls */}

          <div className="season-top-row">
            {/* Inline form */}

            <div className="season-form-row">
              {/* Editing badge */}

              {editId && (
                <span className="season-editing-badge">
                  Editing #{editId}
                </span>
              )}

              {/* Engine capacity input */}

              <input
                type="text"
                placeholder="Enter Engine Capacity"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !saving) {
                    handleSubmit();
                  }
                }}
                className="season-input"
                disabled={saving || loading}
              />

              {/* Cancel edit */}

              {editId && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="season-cancel-btn"
                  disabled={saving || loading}
                >
                  Cancel
                </button>
              )}

              {/* Add or update */}

              <button
                type="button"
                onClick={handleSubmit}
                className="um-btn-primary"
                disabled={saving || loading}
              >
                {saving ? (
                  <span className="spinner" />
                ) : editId ? (
                  <>
                    <FiEdit />
                    Update Engine Capacity
                  </>
                ) : (
                  <>
                    <FiPlus />
                    Add Engine Capacity
                  </>
                )}
              </button>
            </div>

            {/* Search */}

            <div className="search-wrapper">
              <span className="search-icon">
                <FiSearch size={17} />
              </span>

              <input
                type="text"
                className="search-input"
                placeholder="Search Engine Capacity..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                disabled={loading}
              />

              {search && (
                <button
                  type="button"
                  className="season-clear-btn"
                  onClick={() => {
                    setSearch("");
                    setPage(1);
                  }}
                  disabled={loading}
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Table */}

          <div className="table-responsive">
            <table className="table table-striped table-hover table-bordered align-middle mb-0">
              {/* Table header */}

              <thead className="table-light">
                <tr className="um-table-head">
                  <th className="um-th text-nowrap">#</th>

                  <th className="um-th text-nowrap text-center">
                    Engine Capacity
                  </th>

                  <th className="um-th text-nowrap text-center">
                    Action
                  </th>
                </tr>
              </thead>

              {/* Table body */}

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="um-td text-center"
                    >
                      Loading…
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="empty-table-cell text-center py-5"
                    >
                      {search
                        ? "No matching Engine Capacity found"
                        : "No Engine Capacity found"}
                    </td>
                  </tr>
                ) : (
                  data.map((row, i) => {
                    const isEditing =
                      editId === row.mstmod_id;

                    return (
                      <tr
                        key={row.mstmod_id}
                        className={`um-row ${
                          isEditing ? "editing-row" : ""
                        }`}
                      >
                        {/* Number */}

                        <td className="um-td user-number text-center">
                          {(page - 1) * 10 + i + 1}
                        </td>

                        {/* Engine capacity */}

                        <td className="um-td text-center">
                          <span className="season-name-chip">
                            {row.mstmod_val}
                          </span>
                        </td>

                        {/* Actions */}

                        <td className="um-td text-center season-action-td">
                          <div className="action-buttons d-flex justify-content-center align-items-center gap-2">
                            {/* Edit */}

                            <button
                              type="button"
                              onClick={() => handleEdit(row)}
                              className="action-btn action-edit btn btn-sm"
                              title="Edit"
                              disabled={loading}
                            >
                              <FiEdit size={15} />
                            </button>

                            {/* Delete */}

                            <button
                              type="button"
                              onClick={() =>
                                openDeleteModal(row)
                              }
                              className="action-btn action-delete btn btn-sm"
                              title="Delete"
                              disabled={loading || delSaving}
                            >
                              <FiTrash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {/* Pagination */}

            {totalPages > 0 && (
              <div className="pagination">
                {/* Pagination info */}

                <span className="pagin-info">
                  {total > 0
                    ? `Showing ${(page - 1) * 10 + 1}–${Math.min(
                        page * 10,
                        total
                      )} of ${total}`
                    : ""}
                </span>

                {/* Pagination buttons */}

                <div className="d-flex align-items-center gap-2">
                  {/* Previous */}

                  <button
                    type="button"
                    className="pg-btn"
                    disabled={page === 1 || loading}
                    onClick={() =>
                      setPage((p) => Math.max(1, p - 1))
                    }
                  >
                    ← Prev
                  </button>

                  {/* Current page */}

                  <span className="pagin-info">
                    Page {page} / {totalPages}
                  </span>

                  {/* Next */}

                  <button
                    type="button"
                    className="pg-btn"
                    disabled={
                      page >= totalPages || loading
                    }
                    onClick={() =>
                      setPage((p) =>
                        Math.min(totalPages, p + 1)
                      )
                    }
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Delete modal */}

        {delModal && (
          <div
            className="um-overlay"
            onClick={closeDeleteModal}
          >
            <div
              className="um-modal um-delete-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="delete-content">
                {/* Delete icon */}

                <div className="delete-icon">
                  <FiTrash2 size={32} />
                </div>

                {/* Delete title */}

                <h2 className="delete-title">
                  Delete Engine Capacity?
                </h2>

                {/* Delete description */}

                <p className="delete-description">
                  Are you sure you want to delete{" "}
                  <span className="badge badge-success">
                    "{delModal.mstmod_val}"
                  </span>
                  ?
                  <br />
                  This will permanently remove this engine
                  capacity.
                  <br />
                  <strong>
                    This action cannot be undone.
                  </strong>
                </p>

                {/* Delete actions */}

                <div className="delete-actions">
                  {/* Cancel */}

                  <button
                    type="button"
                    className="um-btn-secondary"
                    onClick={closeDeleteModal}
                    disabled={delSaving}
                  >
                    Cancel
                  </button>

                  {/* Confirm delete */}

                  <button
                    type="button"
                    disabled={delSaving}
                    className="delete-confirm-btn"
                    onClick={handleDelete}
                  >
                    {delSaving ? (
                      <span className="spinner" />
                    ) : (
                      <>
                        <FiTrash2 />
                        Delete Permanently
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

       
      </main>
    </Layout>
  );
}